<script setup lang="ts">
import { MediaProps } from '~/components/cms/Media/Media.vue'
import { HeroPanelStandardContentOnlyProps } from './HeroPanelStandardContentOnly.vue'

type HeroPanelStandardProps = HeroPanelStandardContentOnlyProps & {
  media: MediaProps
}

const props = defineProps<HeroPanelStandardProps>()
</script>

<template>
  <section class="hero-panel-standard" data-testid="hero-panel-standard">
    <div class="hero-panel__media">
      <Media v-bind="media" loading="eager" />
    </div>

    <div class="hero-panel__content">
      <div class="hero-panel__content-wrap">
        <p class="hero-panel__tagline" v-if="tagline">{{ tagline }}</p>
        <Markdown class="hero-panel__title" :content="titleMD" tag="h1" />
        <Markdown class="hero-panel__text" :content="text" />
        <Action
          class="hero-panel__action"
          data-testid="hero-panel-standard__action"
          v-if="action"
          v-bind="action"
          :button="{ appearance: 'Button' }"
        />
      </div>
    </div>
  </section>
</template>

<style lang="postcss">
@import '~/assets/css/placeholders.css';

.hero-panel-standard {
  --height: 80vh;
  --content-min-height: 16rem;
  height: var(--height);
  margin-bottom: var(--component-space);
  min-height: 25rem;
  display: grid;
  grid-template-areas: 'fullspan';

  .hero-panel__media,
  .hero-panel__content {
    grid-area: fullspan;
  }

  .hero-panel__content-wrap {
    max-width: var(--text-max-width); /* 416px */
  }

  .hero-panel__media {
    max-height: calc(var(--height) - var(--content-min-height));
    overflow: hidden;

    img {
      height: 100%;
      width: 100%; /* required for centering fit:cover */
      min-width: 100vw;
      object-fit: cover;
    }

    .video {
      height: var(--height);
    }

    video {
      height: var(--height);
      width: 100vw;
      min-width: 20rem;
      object-fit: cover;
    }
  }

  .hero-panel__content {
    min-height: var(--content-min-height);
    align-self: end; /* to the grid area */
    justify-items: center;
    background: var(--surface-light-40);
    padding: var(--space-70) var(--space-50) var(--space-100) var(--space-50);

    p:last-child {
      margin-bottom: 0;
    }
  }

  .hero-panel__tagline {
    letter-spacing: var(--letter-spacing-2);
    text-transform: uppercase;
    font-weight: bold;
    margin-bottom: var(--space-30);
  }

  .hero-panel__title {
    margin-bottom: var(--space-50);

    > p {
      margin-bottom: 0; /* p that may come from Markdown formatting */
    }
  }

  .hero-panel__text {
    @extend %body;
    margin-bottom: var(--space-70);
    max-width: var(--text-max-width); /* 416px */
  }

  @media (--tablet) {
    --height: 70vh;

    .hero-panel__content {
      display: flex;
      align-items: center;
      justify-content: center;
    }
  }

  @media (--desktop) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    grid-template-areas: 'media content';

    .hero-panel__media {
      grid-area: media;
      max-height: none;
      img {
        min-width: 50vw;
      }
    }
    .hero-panel__content {
      grid-area: content;
      height: 100%;
    }
  }
}
</style>
